import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Eye, 
  EyeOff, 
  Check, 
  AlertCircle, 
  Loader, 
  Building2, 
  Shield,
  Plug,
  PlugZap
} from 'lucide-react';

import { getSupabaseClient } from '../supabaseClient';
import { useBusinessType } from '../context/BusinessContext';
import { API_BASE_URL } from '../config/api';

const IntegrationDetails = () => {
  const { providerSlug } = useParams();
  const navigate = useNavigate();
  const { businessType } = useBusinessType();
  const supabase = getSupabaseClient(businessType);

  // Component state
  const [provider, setProvider] = useState(null);
  const [authConfig, setAuthConfig] = useState(null);
  const [existingIntegration, setExistingIntegration] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Form data state
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    storeId: '',
    machineNo: ''
  });

  // Load provider and check for existing integration
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Get current user
        const { data: { user }, error: userError } = await supabase.auth.getUser();
        if (userError) throw userError;

        // Fetch provider details
        const { data: providerData, error: providerError } = await supabase
          .from('api_providers')
          .select('*')
          .eq('slug', providerSlug)
          .eq('is_active', true)
          .single();

        if (providerError) throw providerError;
        setProvider(providerData);

        // Fetch auth configuration
        const { data: authConfigData, error: authConfigError } = await supabase
          .from('auth_provider_configs')
          .select(`
            *,
            auth_methods (
              id,
              name,
              requires_refresh
            )
          `)
          .eq('provider_id', providerData.id)
          .single();

        if (authConfigError) throw authConfigError;
        setAuthConfig(authConfigData);

        // Check for existing integration
        const { data: integrationData, error: integrationError } = await supabase
          .from('accounts_integrations')
          .select('*')
          .eq('account_id', user.id)
          .eq('provider_id', providerData.id)
          .eq('is_active', true)
          .maybeSingle();

        if (integrationError && integrationError.code !== 'PGRST116') throw integrationError;

        if (integrationData) {
          setExistingIntegration(integrationData);

          // Load existing metadata for CCI fields
          if (integrationData.metadata) {
            setFormData(prev => ({
              ...prev,
              storeId: integrationData.metadata.store_id || '',
              machineNo: integrationData.metadata.machine_no || ''
            }));
          }

          // Load existing credentials (show as masked for security)
          const { data: credentialsData, error: credentialsError } = await supabase
            .from('api_credentials')
            .select('credential_type')
            .eq('integration_id', integrationData.id);

          if (!credentialsError && credentialsData) {
            // Mark form as having existing credentials (don't show actual values)
            if (credentialsData.some(c => c.credential_type === 'username')) {
              setFormData(prev => ({ ...prev, username: '••••••••' }));
            }
            if (credentialsData.some(c => c.credential_type === 'password')) {
              setFormData(prev => ({ ...prev, password: '••••••••' }));
            }
          }
        }

      } catch (err) {
        // Error fetching data - handle silently in production
        setError('Failed to load integration details. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    if (providerSlug) {
      fetchData();
    }
  }, [providerSlug, supabase]);

  // Handle form input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
    setSuccess(false);
  };

  // Connect integration (test credentials and save)
  const connectIntegration = async () => {
    try {
      setConnecting(true);
      setError(null);

      // Validate required fields
      if (!formData.username || !formData.password) {
        throw new Error('Username and password are required');
      }

      if (provider.slug.includes('cci') && (!formData.storeId || !formData.machineNo)) {
        throw new Error('Store ID and Machine Number are required for CCI integrations');
      }

      // Don't allow updating with masked passwords
      if (existingIntegration && (formData.username === '••••••••' || formData.password === '••••••••')) {
        throw new Error('Please enter your actual credentials to update the connection');
      }

      // Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;

      // First test the connection via backend
      const testResponse = await fetch(`${API_BASE_URL}/api/integrations/test-connection`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          provider_slug: provider.slug,
          auth_method: 'bearer_token',
          credentials: {
            username: formData.username,
            password: formData.password
          },
          endpoint: `${provider.base_url}${authConfig.config.token_endpoint}`
        })
      });

      if (!testResponse.ok) {
        const errorData = await testResponse.json();
        throw new Error(errorData.error || 'Connection failed. Please check your credentials.');
      }

      const testData = await testResponse.json();

      // Save or update integration in Supabase
      const integrationData = {
        account_id: user.id,
        provider_id: provider.id,
        auth_method_id: authConfig.auth_method_id,
        is_active: true,
        metadata: provider.slug.includes('cci') ? {
          store_id: formData.storeId,
          machine_no: formData.machineNo
        } : {}
      };

      const { data: savedIntegration, error: integrationError } = await supabase
        .from('accounts_integrations')
        .upsert(integrationData, { 
          onConflict: 'account_id,provider_id',
          ignoreDuplicates: false 
        })
        .select()
        .single();

      if (integrationError) throw integrationError;

      // Save credentials
      await supabase
        .from('api_credentials')
        .upsert({
          integration_id: savedIntegration.id,
          credential_type: 'username',
          encrypted_value: formData.username
        }, {
          onConflict: 'integration_id,credential_type',
          ignoreDuplicates: false
        });

      await supabase
        .from('api_credentials')
        .upsert({
          integration_id: savedIntegration.id,
          credential_type: 'password',
          encrypted_value: formData.password
        }, {
          onConflict: 'integration_id,credential_type',
          ignoreDuplicates: false
        });

      // Save token
      await supabase
        .from('api_tokens')
        .upsert({
          integration_id: savedIntegration.id,
          token_type: 'bearer',
          encrypted_token: testData.token,
          expires_at: null // CCI tokens don't expire
        }, {
          onConflict: 'integration_id,token_type',
          ignoreDuplicates: false
        });

      setExistingIntegration(savedIntegration);
      setSuccess('Connected successfully! Redirecting...');

      // Redirect after successful connection
      setTimeout(() => {
        navigate('/connect/apps');
      }, 2000);

    } catch (err) {
      // Error connecting integration - handle silently in production
      setError(err.message || 'Failed to connect. Please check your credentials and try again.');
    } finally {
      setConnecting(false);
    }
  };

  // Disconnect integration
  const disconnectIntegration = async () => {
    if (!confirm('Are you sure you want to disconnect this integration?')) return;

    try {
      setConnecting(true);
      setError(null);

      // Deactivate the integration
      const { error: updateError } = await supabase
        .from('accounts_integrations')
        .update({ is_active: false })
        .eq('id', existingIntegration.id);

      if (updateError) throw updateError;

      setSuccess('Disconnected successfully! Redirecting...');
      setTimeout(() => {
        navigate('/connect/apps');
      }, 1500);

    } catch (err) {
      // Error disconnecting integration - handle silently in production
      setError(err.message || 'Failed to disconnect integration. Please try again.');
    } finally {
      setConnecting(false);
    }
  };

  // Render form fields
  const renderAuthFields = () => {
    return (
      <div className="space-y-4">
        {/* Username */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Username
          </label>
          <input
            type="text"
            value={formData.username}
            onChange={(e) => handleInputChange('username', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
            placeholder="Enter your username"
          />
        </div>

        {/* Password */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Password
          </label>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={(e) => handleInputChange('password', e.target.value)}
              className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
              placeholder="Enter your password"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
            >
              {showPassword ? (
                <EyeOff className="h-4 w-4 text-gray-400" />
              ) : (
                <Eye className="h-4 w-4 text-gray-400" />
              )}
            </button>
          </div>
        </div>

        {/* CCI-specific fields */}
        {provider?.slug.includes('cci') && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Store ID
              </label>
              <input
                type="text"
                value={formData.storeId}
                onChange={(e) => handleInputChange('storeId', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                placeholder="Enter your store ID"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Machine Number
              </label>
              <input
                type="text"
                value={formData.machineNo}
                onChange={(e) => handleInputChange('machineNo', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                placeholder="Enter your machine number"
              />
            </div>
          </>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!provider || !authConfig) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="mx-auto h-12 w-12 text-red-400 mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Integration Not Found</h2>
          <p className="text-gray-600 mb-4">
            The requested integration could not be found or is not available.
          </p>
          <button
            onClick={() => navigate('/connect/apps')}
            className="px-4 py-2 bg-gray-900 text-white rounded-md hover:bg-gray-800 transition-colors"
          >
            Back to Integrations
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/connect/apps')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Integrations
          </button>

          <div className="flex items-center mb-4">
            <div className="bg-white p-3 rounded-full shadow-sm mr-4">
              <Building2 className="w-6 h-6 text-gray-700" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{provider.name}</h1>
              <p className="text-gray-600">
                {existingIntegration 
                  ? `Connected to ${provider.name}` 
                  : `Connect to ${provider.name}`
                }
              </p>
            </div>
          </div>

          {/* Connection Status */}
          {existingIntegration && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <div className="flex items-center">
                <PlugZap className="w-5 h-5 text-green-500 mr-2" />
                <span className="text-sm font-medium text-green-900">
                  Connected Successfully
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Main Content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          {/* Security Notice */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-start">
              <Shield className="w-5 h-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0" />
              <div>
                <h3 className="text-sm font-medium text-blue-900 mb-1">Secure Connection</h3>
                <p className="text-sm text-blue-700">
                  Your credentials are encrypted and securely stored. We verify the connection before saving.
                </p>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                {existingIntegration ? 'Update Connection' : 'Connect Integration'}
              </h2>
              {renderAuthFields()}
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-start">
                  <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 mr-3 flex-shrink-0" />
                  <div>
                    <h3 className="text-sm font-medium text-red-900 mb-1">Connection Failed</h3>
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Success Message */}
            {success && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-start">
                  <Check className="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                  <div>
                    <h3 className="text-sm font-medium text-green-900 mb-1">Success!</h3>
                    <p className="text-sm text-green-700">{success}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3">
              {/* Connect/Update Button */}
              <button
                onClick={connectIntegration}
                disabled={
                  connecting || 
                  (!formData.username || !formData.password) ||
                  (provider?.slug.includes('cci') && (!formData.storeId || !formData.machineNo))
                }
                className="flex items-center px-6 py-2 bg-gray-900 text-white rounded-md hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {connecting ? (
                  <Loader className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <PlugZap className="w-4 h-4 mr-2" />
                )}
                {existingIntegration ? 'Update Connection' : 'Connect'}
              </button>

              {/* Disconnect Button - only for existing connections */}
              {existingIntegration && (
                <button
                  onClick={disconnectIntegration}
                  disabled={connecting}
                  className="flex items-center px-4 py-2 border border-red-300 text-red-700 rounded-md hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {connecting ? (
                    <Loader className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Plug className="w-4 h-4 mr-2" />
                  )}
                  Disconnect
                </button>
              )}
            </div>

            {/* Flow explanation */}
            {!existingIntegration && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start">
                  <PlugZap className="w-5 h-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0" />
                  <div>
                    <h3 className="text-sm font-medium text-blue-900 mb-1">Connection Process</h3>
                    <p className="text-sm text-blue-700">
                      We'll verify your credentials with {provider.name} and securely save your connection.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default IntegrationDetails; 